import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis, Legend
} from 'recharts';
import { CheckCircle, Clock, XCircle } from 'lucide-react';

// Gold-red palette for BCM
const COLORS = ['#f59e0b', '#F87171']; // Yes = gold, No = red

const BCMAssessmentDashboardPage = () => {
  const [report, setReport] = useState({
    phaseScores: [],
    answerStats: { yes: 0, no: 0 },
    overallScore: null,
    overview: { phases: 0, focusAreas: 0, questions: 0 },
    recentActivities: [],
    deadlines: [],
    notifications: [],
    userProfile: { name: '', department: '', email: '' },
    assessments: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    const fetchDashboard = async () => {
      try {
        // Fetch BCM dashboard data
        const [dashboardRes, bcmPhasesRes, overdueRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}maturity/analytics/bcm-dashboard/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}maturity/bcm/phases/?all=true`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}maturity/assessments/dashboard/bcm/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const {
          phase_scores,
          total_yes,
          total_no,
          average_score,
          overview,
          recent_activities,
          deadlines,
          notifications,
          user_profile,
        } = dashboardRes.data;

        const bcmPhases = Array.isArray(bcmPhasesRes.data) ? bcmPhasesRes.data : [];
        const bcmPhaseMap = {};
        phase_scores?.forEach(p => {
          bcmPhaseMap[p.phase_id] = p;
        });

        // Include all phases (even those without scores)
        const mergedPhaseScores = bcmPhases.map(phase => ({
          phase_id: phase.id,
          phase_name: phase.title || `Phase ${phase.id}`,
          phase_code: phase.code || `P${phase.id}`,
          score: bcmPhaseMap[phase.id]?.score ?? 0, // default to 0
        }));

        // Only keep BCM deadlines
        const bcmDeadlines = Array.isArray(deadlines)
          ? deadlines.filter(item => item.name?.toLowerCase().includes('bcm'))
          : [];

        // Process assessments
        const assessmentsData = Array.isArray(overdueRes.data.assessments)
          ? overdueRes.data.assessments.map(a => ({
              ...a,
              is_overdue: a.deadline ? new Date(a.deadline) < new Date() : false,
            }))
          : [];
        const yourAssessments = Array.isArray(dashboardRes.data.your_assessments)
          ? dashboardRes.data.your_assessments
          : [];
        setReport({
          phaseScores: mergedPhaseScores,
          answerStats: { yes: total_yes || 0, no: total_no || 0 },
          overallScore: average_score ?? null,
          overview: overview || { phases: 0, focusAreas: 0, questions: 0 },
          recentActivities: Array.isArray(recent_activities) ? recent_activities : [],
          deadlines: bcmDeadlines,
          notifications: Array.isArray(notifications) ? notifications : [],
          userProfile: user_profile || { name: '', department: '', email: '' },
          assessments: assessmentsData,
          yourAssessments: yourAssessments,
        });

        setLoading(false);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) window.location.href = "/login";
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) return <div className="text-center mt-10 text-amber-700 font-medium">Loading BCM Dashboard...</div>;

  const { phaseScores, answerStats, overallScore, overview, userProfile, recentActivities, deadlines, notifications } = report;

  return (
    <div className="bg-white min-h-screen px-6 py-8 max-w-7xl mx-auto text-gray-800">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-amber-700">Welcome, {userProfile.name || 'User'}!</h1>
        <p className="text-sm text-gray-600">Department: {userProfile.department || 'N/A'}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-8">
        <button
          onClick={() => window.location.href = '/bcm/assessment'}
          className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 transition"
        >
          View Assessment
        </button>
        <button
          onClick={() => window.location.href = '/bcm/report'}
          className="bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600 transition"
        >
          View Report
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border p-5 rounded-lg shadow text-center">
          <p className="text-lg font-semibold">Phases</p>
          <p className="text-3xl text-amber-700">{overview.phases}</p>
        </div>
        <div className="bg-white border p-5 rounded-lg shadow text-center">
          <p className="text-lg font-semibold">Focus Areas</p>
          <p className="text-3xl text-amber-700">{overview.focusAreas}</p>
        </div>
        <div className="bg-white border p-5 rounded-lg shadow text-center">
          <p className="text-lg font-semibold">Questions</p>
          <p className="text-3xl text-amber-700">{overview.questions}</p>
        </div>
      </div>

      {/* Overall Score */}
      <div className="bg-amber-50 p-6 rounded-lg shadow text-center mb-10 border border-amber-200">
        <p className="text-lg font-semibold">Overall Maturity Score</p>
        <p className="text-5xl font-bold text-amber-700">{overallScore ?? 'N/A'}</p>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {/* Phase Maturity Levels */}
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-amber-700 mb-2">Phase Maturity Levels</h2>
          {phaseScores.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={phaseScores.map(p => ({
                  name: `${p.phase_code || ''} ${p.phase_name}`,
                  score: p.score
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              >
                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-sm">No phase data available.</p>
          )}
        </div>

        {/* Response Distribution */}
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-amber-700 mb-2">Response Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Compliant', value: answerStats.yes },
                  { name: 'Non-Compliant', value: answerStats.no },
                ]}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
                dataKey="value"
              >
                {COLORS.map((color, index) => <Cell key={index} fill={color} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity, Deadlines, Notifications, Assessments */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {[
          { title: 'Recent Activity', data: recentActivities, type: 'activity' },
          { title: 'Upcoming Deadlines', data: deadlines, type: 'deadline' },
          { title: 'Notifications', data: notifications, type: 'notification' },
        ].map(({ title, data, type }) => (
          <div key={title} className="bg-white p-5 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-amber-700 mb-2">{title}</h2>
            <ul className="list-disc pl-5 text-gray-700 text-sm">
              {data.length > 0 ? (
                data.map((item, i) => {
                  if (type === 'activity') {
                    return (
                      <li key={i} className="mb-3">
                        <p className="font-medium">{item.assessment_name}</p>
                        {item.submitted_phases?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.submitted_phases.map((phase, idx) => (
                              <span
                                key={idx}
                                className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full"
                              >
                                {phase}
                              </span>
                            ))}
                          </div>
                        )}
                        <p
                          className={`mt-1 font-semibold ${
                            item.status === 'Completed' ? 'text-green-600' : 'text-orange-600'
                          }`}
                        >
                          {item.status}
                        </p>
                      </li>
                    );
                  }

                  if (type === 'deadline') {
                    const deadlineDate = item.deadline
                      ? new Date(item.deadline).toLocaleDateString()
                      : 'N/A';
                    return (
                      <li key={i}>
                        {item.name} — Phase: {item.phase || 'N/A'} — Due: {deadlineDate}
                      </li>
                    );
                  }

                  if (type === 'notification') {
                    return (
                      <li key={i}>
                        <strong>{item.type?.toUpperCase()}:</strong> {item.message}
                      </li>
                    );
                  }

                  return <li key={i}>{item}</li>;
                })
              ) : (
                <li className="text-gray-400">No {title.toLowerCase()}</li>
              )}
            </ul>
          </div>
        ))}

        {/* Your Assessments */}
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-amber-700 mb-3">Your Assessments</h2>

          {report.yourAssessments?.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {report.yourAssessments.map((a) => {
                // Safely format deadline
                const deadlineText = a.deadline
                  ? new Date(a.deadline).toLocaleDateString()
                  : 'N/A';

                // Pick badge color based on status
                let badgeColor =
                  a.status?.toLowerCase().includes('submitted')
                    ? 'bg-blue-500'
                    : a.status?.toLowerCase().includes('overdue')
                    ? 'bg-red-600'
                    : 'bg-green-500';

                return (
                  <li
                    key={a.id}
                    className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{a.name}</span>{' '}
                      <span className="text-gray-500">— Due: {deadlineText}</span>
                    </div>
                    <span
                      className={`mt-1 sm:mt-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-white rounded-full ${badgeColor}`}
                    >
                      {a.status?.toLowerCase().includes('submitted') && <CheckCircle size={12} />}
                      {a.status?.toLowerCase().includes('on time') && <Clock size={12} />}
                      {a.status?.toLowerCase().includes('overdue') && <XCircle size={12} />}
                      {a.status || 'Pending'}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-400 text-sm">No assessments available</p>
          )}
        </div>
      </div>

      {/* User Profile */}
      <div className="bg-white p-5 rounded-lg shadow mb-10">
        <h2 className="text-lg font-semibold text-amber-700 mb-2">User Profile</h2>
        <p>
          <strong>Name:</strong> {userProfile.name || 'N/A'}
        </p>
        <p>
          <strong>Department:</strong> {userProfile.department || 'N/A'}
        </p>
        <p>
          <strong>Email:</strong> {userProfile.email || 'N/A'}
        </p>
      </div>
    </div>
  );
};

export default BCMAssessmentDashboardPage;
